import { useEffect, useMemo, useRef, useState } from 'react';
import { Navigate, useParams, useSearchParams } from 'react-router-dom';
import { Box, Grid, GridItem, Tab, TabList, TabPanel, TabPanels, Tabs, useColorModeValue } from '@chakra-ui/react';
import { PageContainer } from '@/client/components/layout/PageContainer';
import { useAuth } from '@/lib/sdk/auth/hooks';
import { helpers } from '@/client/utils/helpers';
import { useGuideEntries } from '@/lib/sdk/ai-guide';
import { GuideEntryList } from '../components/GuideEntryList';
import { GuideEntryView } from '../components/GuideEntryView';
import { AskPanel } from '../components/AskPanel';
import { SolvePanel } from '../components/SolvePanel';
import { SolveSessions } from '../components/SolveSessions';

/**
 * The workflow guide: every entry of the AI assistant's catalog, Ask mode, and Solve
 * mode on its own tab (?mode=solve). The open entry is in the URL (?entry=), so
 * citations and links can point at it; a citation in a plan opens the Guide tab on it.
 * The Solve session stays in the URL (?solve=) on both tabs, so Back from Build or a
 * reload returns to the plan. The list of recent sessions switches between them.
 */
export function GuidePage() {
  const { orgId } = useParams<{ orgId: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const { user, currentOrg } = useAuth();
  const { data: entries, isLoading } = useGuideEntries();
  const panelBg = useColorModeValue('white', 'gray.800');

  const titles = useMemo(
    () => Object.fromEntries((entries ?? []).map((entry) => [entry.id, entry.title])),
    [entries]
  );
  const selectedId = searchParams.get('entry');
  const solveSessionId = searchParams.get('solve');
  // The session in the Solve panel. A new key remounts the panel on it: when the user
  // picks a session, or Back/Forward changes the URL's session. Creating a session
  // doesn't remount the panel while its first message is in flight. Kept here, not only
  // in the URL, because the router applies a URL change after this state.
  const [opened, setOpened] = useState(() => ({ sessionId: solveSessionId, key: 0 }));
  const [urlSessionId, setUrlSessionId] = useState(solveSessionId);
  if (solveSessionId !== urlSessionId) {
    setUrlSessionId(solveSessionId);
    if (solveSessionId !== opened.sessionId) {
      setOpened({ sessionId: solveSessionId, key: opened.key + 1 });
    }
  }
  const withSession = (params: Record<string, string>) => (solveSessionId ? { ...params, solve: solveSessionId } : params);
  const openEntry = (entryId: string) => setSearchParams(withSession({ entry: entryId }));
  const solving = searchParams.get('mode') === 'solve';
  const changeTab = (index: number) => setSearchParams(withSession(index === 1 ? { mode: 'solve' } : {}));
  // A panel replaced by another session while creating one doesn't take over the URL.
  // Its callback is from its own render, so the current key is read from a ref.
  const currentKey = useRef(opened.key);
  useEffect(() => {
    currentKey.current = opened.key;
  }, [opened.key]);
  const keepSession = (sessionId: string, panelKey: number) => {
    if (panelKey !== currentKey.current) return;
    setOpened((previous) => ({ ...previous, sessionId }));
    setSearchParams({ mode: 'solve', solve: sessionId }, { replace: true });
  };
  const switchSession = (sessionId: string | null) => {
    setSearchParams(sessionId ? { mode: 'solve', solve: sessionId } : { mode: 'solve' });
    setOpened((previous) => ({ sessionId, key: previous.key + 1 }));
  };

  // FeatureGuard on this route checks any workflows permission; the org's ai_chat
  // feature has no per-user grant, so it is checked on the org (as on the AI page).
  if (!(user?.is_super_admin ?? false) && !helpers.hasFeatureAccess(currentOrg?.feature_access, 'ai_chat')) {
    return <Navigate to={`/dashboard/${orgId}/workflows`} replace />;
  }

  const breadcrumbs = [
    { label: 'Dashboard', href: `/dashboard/${orgId}/reports` },
    { label: 'Workflows', href: `/dashboard/${orgId}/workflows` },
    { label: 'Guide' },
  ];

  return (
    <PageContainer title="Workflow guide" description="How to set up every workflow feature" breadcrumbs={breadcrumbs}>
      {/* Solve mounts on first use and stays mounted, so its plan survives opening a cited entry */}
      <Tabs index={solving ? 1 : 0} onChange={changeTab} isLazy lazyBehavior="keepMounted" variant="enclosed">
        <TabList>
          <Tab data-testid="guide-tab-guide">Guide</Tab>
          <Tab data-testid="guide-tab-solve">Solve a requirement</Tab>
        </TabList>
        <TabPanels>
          <TabPanel px={0}>
            <Grid
              data-testid="guide-page"
              templateColumns={{ base: '1fr', lg: '260px minmax(0, 1fr) 360px' }}
              gap={4}
              alignItems="start"
            >
              <GridItem bg={panelBg} borderRadius="lg" p={3}>
                <GuideEntryList entries={entries} isLoading={isLoading} selectedId={selectedId} onSelect={openEntry} />
              </GridItem>
              <GridItem bg={panelBg} borderRadius="lg" p={4} minH="400px">
                <GuideEntryView entryId={selectedId} titles={titles} onOpenEntry={openEntry} />
              </GridItem>
              {/* On one column (mobile) Ask comes first, not below every entry */}
              <GridItem bg={panelBg} borderRadius="lg" p={3} order={{ base: -1, lg: 0 }}>
                <Box position={{ lg: 'sticky' }} top={4}>
                  <AskPanel titles={titles} onOpenEntry={openEntry} />
                </Box>
              </GridItem>
            </Grid>
          </TabPanel>
          <TabPanel px={0}>
            <Grid templateColumns={{ base: '1fr', lg: '260px minmax(0, 1fr)' }} gap={4} alignItems="start" maxW="1380px">
              <GridItem bg={panelBg} borderRadius="lg" p={3}>
                <SolveSessions
                  currentId={opened.sessionId}
                  onOpen={(sessionId) => sessionId !== opened.sessionId && switchSession(sessionId)}
                  onNew={() => switchSession(null)}
                  onDeleted={(sessionId) => sessionId === opened.sessionId && switchSession(null)}
                />
              </GridItem>
              <GridItem bg={panelBg} borderRadius="lg" p={4}>
                <SolvePanel
                  key={opened.key}
                  titles={titles}
                  onOpenEntry={openEntry}
                  initialSessionId={opened.sessionId}
                  onSessionCreated={(sessionId) => keepSession(sessionId, opened.key)}
                />
              </GridItem>
            </Grid>
          </TabPanel>
        </TabPanels>
      </Tabs>
    </PageContainer>
  );
}
